import React, { useEffect, useState, useRef } from "react";
import { FormControl, Select, MenuItem, IconButton } from '@mui/material';
import { Paper } from "@material-ui/core";
import { Tabs, Tab } from "@material-ui/core";
import { Button, Input } from '@material-ui/core';
import { pInputSource } from "../../utils/parameters";
import { useStreamProvider } from "../../api/StreamProvider";
import { SourceRecorder } from "./SourceRecorder";
import { get } from "lodash";
import PhotoCameraFrontIcon from '@mui/icons-material/PhotoCameraFront';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import ReplayIcon from '@mui/icons-material/Replay';
import RefreshIcon from '@mui/icons-material/Refresh';

export function SourceSelection(props) {
    const {enableWebcam, enableVideo, deviceId, setDeviceId} = props

    const [ inputSource, setInputSource ] = useState(pInputSource.VIDEO)
    const [ arrWebcamDeviceIdsAvailable, setArrWebcamDeviceIdsAvailable ] = useState([]);  // List devices
    const [ webcamDeviceSelectedIdx, setWebcamDeviceSelectedIdx ] = useState(-1)
    const { 
        closeCurrentStreams, 
        streamVideoWidthRef, 
        streamVideoHeightRef, 
        streamVideoOriginal,
        setStreamVideoOriginal,
    } = useStreamProvider()

    const deviceSelect_reference = useRef(null);
    const currentVideoFileRef = useRef(null);
    const videoObjectUrlRef = useRef(null);
    const videoElementRef = useRef(null);
    const imageElementRef = useRef(null);
    const canvasRef = useRef(null);
    const contextRef = useRef(null);

    // Init useEffect - init selection video or webcam.
    useEffect(() => {
        if (enableVideo && !enableWebcam){
            setInputSource(pInputSource.VIDEO)
        }else{
            setInputSource(pInputSource.WEBCAM)
        }
        return () => {
            closeCurrentStreams()
        }
    }, [enableVideo, enableWebcam])

    // Change input source
    useEffect(() => {
        resetCommonReferences()
        if (inputSource === pInputSource.WEBCAM){
            updateWebcamArrVideoDevicesAvailable()
        }
        if (inputSource === pInputSource.VIDEO){
            setArrWebcamDeviceIdsAvailable([])
        }
    }, [inputSource])

    // Tras actualizar arrDevideIdsAvailable loadWebcaVideo.
    useEffect(() => {
        console.log("🚀 ~ SourceSelection ~ arrWebcamDeviceIdsAvailable:", arrWebcamDeviceIdsAvailable)
        if (arrWebcamDeviceIdsAvailable.length !== 0){
            const position = arrWebcamDeviceIdsAvailable.findIndex(arrWebcamDeviceIdsAvailable => arrWebcamDeviceIdsAvailable.deviceId === deviceId);
            if (position !== -1){  //  If deviceId not available - Use default first element.
                setWebcamDeviceSelectedIdx(position)
            }else{
                setDeviceId(arrWebcamDeviceIdsAvailable[0]?.deviceId)
                setWebcamDeviceSelectedIdx(0)
            }
            loadWebcamVideo()
        }
    }, [arrWebcamDeviceIdsAvailable])


    useEffect(() => {
        if (deviceId !== null){
            // handleLoadStream()
            // setStreamVideoOriginal(webcamRef.current.stream)
            loadWebcamVideo()
        }
    }, [deviceId])


    async function updateWebcamArrVideoDevicesAvailable() {
        try {
            const devices = await navigator.mediaDevices.enumerateDevices();
            const videoDevices = devices.filter(device => device.kind === 'videoinput');
            setArrWebcamDeviceIdsAvailable(videoDevices)
            if (videoDevices.length !== 0){
                try{
                    const position = videoDevices.findIndex(videoDevices => videoDevices.deviceId === deviceId);
                    if (position !== -1){  //  If actual deviceId not aviable - Use default first element.
                        setDeviceId(arrWebcamDeviceIdsAvailable[0]?.deviceId)
                    }
                }catch (error) {
                    console.error(": ", error);
                }
            }
        } catch (error) {
            console.error("UpdateWebcamArrVideoDevicesAvailable: ", error);
        }
    }

    const loadWebcamVideo = async () => {
        console.log("🚀 ~ loadWebcamVideo ~ loadWebcamVideo:", loadWebcamVideo)
        try{
            const streamWebcam = await navigator.mediaDevices.getUserMedia({
                deviceId: {exact: deviceId},
                width: { exact: 640 },
                height: { exact: 480 },
                video: true,
                audio: false,
            });
            console.log('USAR STREAM NATIVO')
            setStreamVideoOriginal(streamWebcam);
        } catch  (error) {
            console.error("loadWebcamVideo: ", error);
        }

    };

    function selectWebcamVideo(event){
        const selectedIndex = event.target.value;
        const selectedDevice = arrWebcamDeviceIdsAvailable[selectedIndex];
        setDeviceId(selectedDevice)
        setWebcamDeviceSelectedIdx(selectedIndex)
        console.log("selectWebcamVideo - Selected device: ", selectedDevice);
    }


    const loadVideoFile = (file) => {
        currentVideoFileRef.current = file;
        if (videoObjectUrlRef.current) {
            URL.revokeObjectURL(videoObjectUrlRef.current);
        }

        videoObjectUrlRef.current = URL.createObjectURL(file);
        videoElementRef.current = document.createElement('video');
        videoElementRef.current.src = videoObjectUrlRef.current;
        videoElementRef.current.muted = true; 
        canvasRef.current = document.createElement('canvas');
        contextRef.current = canvasRef.current.getContext('2d');
        canvasRef.current.width = streamVideoWidthRef.current; // Establecer el ancho deseado
        canvasRef.current.height = streamVideoHeightRef.current; // Establecer el alto deseado
        videoElementRef.current.onloadedmetadata = () => {
            videoElementRef.current.play(); // Reproduce el video cuando esté listo
        };

        videoElementRef.current.onplay = () => {
            // Función para actualizar y dibujar el video en el canvas
            const drawVideo = () => {
                if (videoElementRef.current !== null){
                    if (!videoElementRef.current.paused && !videoElementRef.current.ended) {
                        contextRef.current.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
                        contextRef.current.drawImage(videoElementRef.current, 0, 0, canvasRef.current.width, canvasRef.current.height);
                        requestAnimationFrame(drawVideo);
                    }
                }
            };
            drawVideo();
            setStreamVideoOriginal(canvasRef.current.captureStream());
        };

        videoElementRef.current.onended = () => {
            videoElementRef.current.load(); // Carga el video desde el principio cuando ha terminado
        };

        videoElementRef.current.onerror = () => {
            console.error(`Error loading video file: ${file.name}`);
        };
    };

    const restartVideo = () => {
        if (currentVideoFileRef.current) {
            loadVideoFile(currentVideoFileRef.current); // Vuelve a cargar el archivo de video referenciado
        }
    };


    const resetCommonReferences = () => {
        if (videoElementRef.current) {
            videoElementRef.current.pause();
        }
        if (videoObjectUrlRef.current) {
            URL.revokeObjectURL(videoObjectUrlRef.current);
        }
        videoElementRef.current = null;
        imageElementRef.current = null;
        canvasRef.current = null;
        contextRef.current = null;
        // closeCurrentStreams();
    };

    const changeInputSource = () => {
        if (inputSource === pInputSource.WEBCAM){
            setInputSource(pInputSource.VIDEO)
        } else {
            setInputSource(pInputSource.WEBCAM)
        }
    }


    // Paso 1: Definir un array de resoluciones comunes a probar
    const commonResolutions = [
            { width: 320, height: 240 },
            { width: 480, height: 360 },
            { width: 640, height: 480 },
            { width: 800, height: 600 },
            { width: 1024, height: 768 },
            { width: 1280, height: 720 },
            { width: 1280, height: 800 },
            { width: 1280, height: 1024 },
            { width: 1366, height: 768 },
            { width: 1440, height: 900 },
            { width: 1600, height: 900 },
            { width: 1920, height: 1080 },
            { width: 2048, height: 1080 },
            { width: 2560, height: 1440 },
            { width: 3840, height: 2160 },
            // Añadir más resoluciones según sea necesario
            // Resoluciones comunes de iPhone
            { width: 640, height: 1136 }, // iPhone 5
            { width: 750, height: 1334 }, // iPhone 6, 6s, 7, 8
            { width: 1242, height: 2208 }, // iPhone 6 Plus, 6s Plus, 7 Plus, 8 Plus
            { width: 1125, height: 2436 }, // iPhone X, XS
            { width: 828, height: 1792 }, // iPhone XR, 11
            { width: 1242, height: 2688 }, // iPhone XS Max, 11 Pro Max
            { width: 1170, height: 2532 }, // iPhone 12, 12 Pro
            { width: 1284, height: 2778 }, // iPhone 12 Pro Max
            // Resoluciones comunes de Android (ejemplos genéricos, pueden variar)
            { width: 720, height: 1280 }, // HD
            { width: 1080, height: 1920 }, // Full HD
            { width: 1440, height: 2560 }, // QHD
            { width: 1080, height: 2340 }, // 19.5:9 aspect ratio
            { width: 1440, height: 2960 }, // QHD+ in 18.5:9 aspect ratio
    ];
    // Paso 2: Crear la función testResolutions
    const testResolutions = async () => {
        for (let resolution of commonResolutions) {
          const constraints = {
            video: {
              width: { exact: resolution.width },
              height: { exact: resolution.height }
            }
          };
      
          try {
            // Paso 4: Usar getUserMedia para probar la resolución
            const stream = await navigator.mediaDevices.getUserMedia(constraints);
            // Obtener el track de video
            const videoTrack = stream.getVideoTracks()[0];
            // Obtener las configuraciones actuales del track
            const settings = videoTrack.getSettings();
      
            // Verificar si las dimensiones del video coinciden con las solicitadas
            if (settings.width === resolution.width && settings.height === resolution.height) {
              console.log(`Resolución soportada: ${resolution.width}x${resolution.height}`);
            } else {
              console.log(`Resolución reportada como soportada, pero no coincide al probar: ${resolution.width}x${resolution.height}`);
            }
          } catch (error) {
            console.log(`Resolución no soportada: ${resolution.width}x${resolution.height}`);
          }
        }
    };



    return (
        <>
        <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', width: '100%' }}>
        
            { enableWebcam && enableVideo && (
                <Button className="sig_Main_IconButton" variant="outlined" onClick={changeInputSource}> {inputSource === pInputSource.WEBCAM ? <PhotoCameraFrontIcon/> : <VideoFileIcon/>} </Button>
            )}
            

            {inputSource === pInputSource.WEBCAM && (
                <>
                <div className={`sig_ProfileSettings_CameraSel sig_Main_IconButton sig_Main_CameraSelectButton`} style={{ display: 'none' }} data-tour="tourCam_2b">
                {/* <div className={`sig_ProfileSettings_CameraSel sig_Main_IconButton sig_Main_CameraSelectButton`} data-tour="tourCam_2b"> */}

                {/* <div className={`sig_ProfileSettings_CameraSel sig_Main_IconButton sig_Main_CameraSelectButton`} data-tour="tourCam_2b"> */}
                    <>
                            <FormControl fullWidth>
                                <Select
                                    ref={deviceSelect_reference}
                                    onChange={selectWebcamVideo}
                                    value={webcamDeviceSelectedIdx}
                                    displayEmpty
                                    inputProps={{ 'aria-label': 'Without label' }}
                                >
                                    {arrWebcamDeviceIdsAvailable.map((device, index) => (
                                        <MenuItem key={index} value={index}>{device.label}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <IconButton onClick={updateWebcamArrVideoDevicesAvailable}><RefreshIcon fontSize="small"/></IconButton>

                            </>    
                </div>
                
                </>
                
               
            )}
            

            {inputSource === pInputSource.VIDEO && (
                    <div className={`sig_ProfileSettings_CameraSel sig_Main_IconButton sig_Main_CameraSelectButton`} data-tour="tourCam_2b">
                        <FormControl fullWidth>
                            <Input
                                type="file"
                                inputProps={{ accept: 'video/*', style: { display: 'none' } }}
                                onChange={(e) => loadVideoFile(e.target.files[0])}
                                id="fileInput"
                            />
                            <label htmlFor="fileInput">
                                <Button component="span" className={`sig_ProfileSettings_CameraSel sig_Main_IconButton sig_Main_CameraSelectButton`} style={{width:'100px'}}>
                                    Seleccionar Archivo
                                </Button>
                            </label>
                        </FormControl>
                    </div>
            )} 

            {/* { streamVideoOut !== null && <SourceRecorder stream={streamVideoOut}/> } */}

            
        </div>
        </>
    )
}